import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, CircularProgress, Alert, Grid, Card, CardContent,
    Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select,
    MenuItem, InputLabel, FormControl, Stack, IconButton, Divider
} from '@mui/material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
    Add as AddIcon, 
    Send as SendIcon, 
    ArrowBack as ArrowBackIcon, 
    Edit as EditIcon, 
    Delete as DeleteIcon, 
    Visibility as VisibilityIcon, 
    AccountBalanceWallet as AccountBalanceWalletIcon, 
    Groups as GroupsIcon, 
    Description as DescriptionIcon, 
    Timer as TimerIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';

// --- Tab Panels ---
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}
function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && (
                <Box sx={{ py: 3 }}>{children}</Box>
            )}
        </div>
    );
}

const SaaSAdminDashboard = () => {
    // ---- STATE ----
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Data State
    const [stats, setStats] = useState<any>(null);
    const [tenants, setTenants] = useState<any[]>([]);
    const [invoices, setInvoices] = useState<any[]>([]);
    const [plans, setPlans] = useState<any[]>([]);
    
    // Dialog States: Tenants
    const [manageTenantOpen, setManageTenantOpen] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState<any>(null);
    const [tenantForm, setTenantForm] = useState({ plan: '', status: '' });
    
    // Dialog States: Plans
    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<any>(null);
    const [planForm, setPlanForm] = useState({ name: '', code: '', price: 0, maxUsers: 0, maxStudents: 0 });

    // Dialog States: Invoices
    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
    const [invoiceForm, setInvoiceForm] = useState({ 
        tenantId: '', 
        amount: 0, 
        periodStart: '', 
        periodEnd: '',
        planCode: '' 
    });
    
    // Detailed Tenant View States
    const [viewingTenantId, setViewingTenantId] = useState<string | null>(null);
    const [tenantDetails, setTenantDetails] = useState<any>(null);
    const [viewTenantInvoices, setViewTenantInvoices] = useState<any[]>([]);
    
    const [editTenantDialogOpen, setEditTenantDialogOpen] = useState(false);
    const [editTenantForm, setEditTenantForm] = useState({
        name: '', slug: '', domain: '', status: '', plan: '',
        maxUsers: 0, maxStudents: 0
    });

    const [extendDialogOpen, setExtendDialogOpen] = useState(false);
    const [extendDate, setExtendDate] = useState('');

    // ---- FETCHING ----
    const fetchData = async () => {
        try {
            setLoading(true);
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };
            
            const [statsRes, tenantsRes, plansRes, invRes] = await Promise.all([
                axios.get(getApiUrl('saas-admin/dashboard'), { headers }),
                axios.get(getApiUrl('saas-admin/tenants'), { headers }),
                axios.get(getApiUrl('saas-admin/plans'), { headers }),
                axios.get(getApiUrl('saas-admin/invoices'), { headers }),
            ]);
            
            setStats(statsRes.data);
            setTenants(tenantsRes.data);
            setPlans(plansRes.data);
            setInvoices(invRes.data);
            setError('');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Access Denied: SaaS Admin Only');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ---- ACTIONS: TENANTS ----
    const fetchTenantDetails = async (id: string) => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            const [detailsRes, invRes] = await Promise.all([
                axios.get(getApiUrl(`saas-admin/tenants/${id}`), { headers }),
                axios.get(getApiUrl(`saas-admin/invoices`), { 
                    params: { tenantId: id },
                    headers 
                })
            ]);

            setTenantDetails(detailsRes.data);
            setViewTenantInvoices(invRes.data);
            setViewingTenantId(id);
        } catch (err: any) {
            alert('Failed to fetch tenant details');
        } finally {
            // Loading handled by parent or omitted if not needed
        }
    };

    const handleDeleteTenant = async (id: string) => {
        if (!window.confirm('CRITICAL: Are you sure you want to PERMANENTLY delete this tenant and ALL associated data? This action cannot be undone.')) return;
        
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            await axios.delete(getApiUrl(`saas-admin/tenants/${id}`), { headers });
            setViewingTenantId(null);
            fetchData();
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to delete tenant');
        }
    };

    const handleOpenEditTenant = (tenant: any) => {
        setEditTenantForm({
            name: tenant.name,
            slug: tenant.slug,
            domain: tenant.domain || '',
            status: tenant.status,
            plan: tenant.plan,
            maxUsers: tenant.maxUsers,
            maxStudents: tenant.maxStudents
        });
        setSelectedTenant(tenant);
        setEditTenantDialogOpen(true);
    };

    const handleUpdateTenant = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            await axios.put(getApiUrl(`saas-admin/tenants/${selectedTenant.id}`), editTenantForm, { headers });
            setEditTenantDialogOpen(false);
            if (viewingTenantId) fetchTenantDetails(viewingTenantId);
            fetchData();
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to update tenant');
        }
    };

    const handleSaveTenant = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            if (tenantForm.plan !== selectedTenant.plan) {
                await axios.put(getApiUrl(`saas-admin/tenants/${selectedTenant.id}/plan`), { plan: tenantForm.plan }, { headers });
            }
            if (tenantForm.status !== selectedTenant.status) {
                await axios.put(getApiUrl(`saas-admin/tenants/${selectedTenant.id}/status`), { status: tenantForm.status }, { headers });
            }
            
            setManageTenantOpen(false);
            fetchData();
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to update tenant');
        }
    };

    const handleToggleStatus = async (newStatus: string) => {
        if (!window.confirm(`Are you sure you want to change this tenant status to ${newStatus}?`)) return;
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            await axios.put(getApiUrl(`saas-admin/tenants/${viewingTenantId}/status`), { status: newStatus }, { headers });
            if (viewingTenantId) fetchTenantDetails(viewingTenantId);
            fetchData();
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to update status');
        }
    };

    const handleExtendExpiry = async () => {
        if (!extendDate) return;
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            await axios.put(getApiUrl(`saas-admin/tenants/${viewingTenantId}`), { trialEndDate: extendDate }, { headers });
            setExtendDialogOpen(false);
            if (viewingTenantId) fetchTenantDetails(viewingTenantId);
            fetchData();
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to extend expiry');
        }
    };

    const handleResendWelcomeEmail = async (id: string) => {
        if (!window.confirm('Are you sure you want to resend the welcome email with access instructions to this tenant?')) return;
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            await axios.post(getApiUrl(`saas-admin/tenants/${id}/resend-welcome`), {}, { headers });
            alert('Welcome email resent successfully.');
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to resend welcome email');
        }
    };


    // ---- ACTIONS: INVOICES ----
    const handleCreateInvoice = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            await axios.post(getApiUrl(`saas-admin/invoices`), invoiceForm, { headers });
            setInvoiceDialogOpen(false);
            fetchData();
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to create invoice');
        }
    };

    const handleRecordPayment = async (id: string) => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            await axios.put(getApiUrl(`saas-admin/invoices/${id}/paid`), {}, { headers });
            fetchData();
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to record payment');
        }
    };

    const handleSendStatement = async (id: string) => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            await axios.post(getApiUrl(`saas-admin/invoices/${id}/send`), {}, { headers });
            alert('Statement emailed successfully to the school administrator!');
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to send statement');
        }
    };

    // ---- ACTIONS: PLANS ----
    const handleOpenPlanDialog = (plan: any = null) => {
        if (plan) {
            setSelectedPlan(plan);
            setPlanForm({ name: plan.name, code: plan.code, price: plan.price, maxUsers: plan.maxUsers, maxStudents: plan.maxStudents });
        } else {
            setSelectedPlan(null);
            setPlanForm({ name: '', code: '', price: 0, maxUsers: 0, maxStudents: 0 });
        }
        setPlanDialogOpen(true);
    };

    const handleSavePlan = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const headers = { Authorization: `Bearer ${token}` };

            if (selectedPlan) {
                await axios.put(getApiUrl(`saas-admin/plans/${selectedPlan.id}`), planForm, { headers });
            } else {
                await axios.post(getApiUrl(`saas-admin/plans`), planForm, { headers });
            }
            setPlanDialogOpen(false);
            fetchData();
        } catch (err: any) {
            alert(err.response?.data?.message || 'Failed to save plan');
        }
    };

    // ---- HELPERS ----
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ACTIVE': case 'PAID': return 'success';
            case 'TRIAL': return 'info';
            case 'SUSPENDED': case 'OVERDUE': case 'UNPAID': return 'error';
            default: return 'default';
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (error) return (
        <Box sx={{ p: 4 }}>
            <Alert severity="error">{error}</Alert>
        </Box>
    );

    const planChartData = plans.map(p => {
        const count = tenants.filter(t => t.plan === p.code).length;
        return { name: p.name, Tenants: count, Revenue: count * p.price };
    });

    return (
        <Box sx={{ bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)', p: { xs: 2, md: 4 } }}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" fontWeight={800} color="#263B7C">SaaS Administration</Typography>
                    <Typography variant="body2" color="text.secondary">Manage tenants, billing, and platform plans globally.</Typography>
                </Box>
                <Button variant="contained" sx={{ bgcolor: '#F9B43F', color: '#1a285a', fontWeight: 'bold' }} onClick={fetchData}>
                    Refresh Data
                </Button>
            </Stack>

            {/* KPI Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #edf2f7' }}>
                        <CardContent>
                            <Typography color="text.secondary" fontWeight={600} gutterBottom>Total Tenants</Typography>
                            <Typography variant="h3" fontWeight={800} color="#263B7C">{stats?.kpis?.totalTenants || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #edf2f7' }}>
                        <CardContent>
                            <Typography color="text.secondary" fontWeight={600} gutterBottom>Active Subscriptions</Typography>
                            <Typography variant="h3" fontWeight={800} color="#48BB78">{stats?.kpis?.activeTenants || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #edf2f7' }}>
                        <CardContent>
                            <Typography color="text.secondary" fontWeight={600} gutterBottom>Trial Accounts</Typography>
                            <Typography variant="h3" fontWeight={800} color="#ED8936">{stats?.kpis?.trialTenants || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ borderRadius: 3, backgroundImage: 'linear-gradient(135deg, #263B7C 0%, #1a285a 100%)', color: 'white' }}>
                        <CardContent>
                            <Typography sx={{ opacity: 0.8 }} fontWeight={600} gutterBottom>Monthly Recurring Revenue</Typography>
                            <Typography variant="h3" fontWeight={800} color="#F9B43F">${stats?.kpis?.mrr || '0.00'}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Main Tabs */}
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #edf2f7', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'white', px: 2 }}>
                    <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ '& .MuiTabs-indicator': { bgcolor: '#F9B43F', height: 3 } }}>
                        <Tab label="OVERVIEW" sx={{ fontWeight: 600 }} />
                        <Tab label="TENANTS DIRECTORY" sx={{ fontWeight: 600 }} />
                        <Tab label="SaaS BILLING" sx={{ fontWeight: 600 }} />
                        <Tab label="SUBSCRIPTION PLANS" sx={{ fontWeight: 600 }} />
                    </Tabs>
                </Box>

                <Box sx={{ p: 3, bgcolor: '#ffffff' }}>
                    {/* TAB 0: OVERVIEW */}
                    <CustomTabPanel value={tabValue} index={0}>
                        <Grid container spacing={4}>
                            <Grid size={{ xs: 12, md: 8 }}>
                                <Typography variant="h6" fontWeight={700} color="#263B7C" mb={2}>Plan Distribution & Revenue</Typography>
                                <Box sx={{ height: 350, p: 2, border: '1px solid #edf2f7', borderRadius: 2 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={planChartData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                            <XAxis dataKey="name" axisLine={false} tickLine={false} />
                                            <YAxis yAxisId="left" axisLine={false} tickLine={false} />
                                            <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} />
                                            <RechartsTooltip cursor={{ fill: '#F4F7FE' }} />
                                            <Legend />
                                            <Bar yAxisId="left" dataKey="Tenants" fill="#F9B43F" radius={[4, 4, 0, 0]} barSize={40} />
                                            <Bar yAxisId="right" dataKey="Revenue" fill="#263B7C" radius={[4, 4, 0, 0]} barSize={40} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant="h6" fontWeight={700} color="#263B7C" mb={2}>Critical Alerts</Typography>
                                <Stack spacing={2} mb={4}>
                                    {stats?.expiringSoon && stats.expiringSoon.length > 0 ? (
                                        stats.expiringSoon.map((t: any) => (
                                            <Alert 
                                                key={t.id} 
                                                severity="warning" 
                                                variant="outlined" 
                                                sx={{ 
                                                    borderRadius: 2, 
                                                    border: '1px solid #edaf36',
                                                    cursor: 'pointer',
                                                    '&:hover': { bgcolor: 'rgba(237, 175, 54, 0.05)' }
                                                }}
                                                onClick={() => { setTabValue(1); fetchTenantDetails(t.id); }}
                                            >
                                                <Typography variant="subtitle2" fontWeight="bold">{t.name}</Typography>
                                                <Typography variant="caption">
                                                    {t.status} - {t.plan} expires in {Math.ceil((new Date(t.trialEndDate).getTime() - new Date().getTime()) / (1000 * 3600 * 24))} days
                                                </Typography>
                                            </Alert>
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">No immediate expirations detected.</Typography>
                                    )}
                                </Stack>

                                <Typography variant="h6" fontWeight={700} color="#263B7C" mb={2}>Recent Signups</Typography>
                                <Stack spacing={2}>
                                    {stats?.recentTenants?.map((t: any) => (
                                        <Box 
                                            key={t.id} 
                                            sx={{ 
                                                p: 2, border: '1px solid #edf2f7', borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                cursor: 'pointer', '&:hover': { bgcolor: '#F8FAFC' }
                                            }}
                                            onClick={() => { setTabValue(1); fetchTenantDetails(t.id); }}
                                        >
                                            <Box>
                                                <Typography fontWeight={700}>{t.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{new Date(t.createdAt).toLocaleDateString()}</Typography>
                                            </Box>
                                            <Chip label={t.plan} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                        </Box>
                                    ))}
                                </Stack>
                            </Grid>
                        </Grid>
                    </CustomTabPanel>

                    {/* TAB 1: TENANTS */}
                    <CustomTabPanel value={tabValue} index={1}>
                        {viewingTenantId && tenantDetails ? (
                            <Box>
                                <Button startIcon={<ArrowBackIcon />} onClick={() => setViewingTenantId(null)} sx={{ mb: 3, color: '#263B7C' }}>
                                    Back to Directory
                                </Button>
                                
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                                    <Box>
                                        <Typography variant="h4" fontWeight={800} color="#263B7C">{tenantDetails.name}</Typography>
                                        <Typography color="text.secondary">Detailed performance and configuration for {tenantDetails.slug}</Typography>
                                    </Box>
                                    <Stack direction="row" spacing={2}>
                                        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => handleOpenEditTenant(tenantDetails)}>
                                            Edit Info
                                        </Button>
                                        <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => handleDeleteTenant(tenantDetails.id)}>
                                            Delete
                                        </Button>
                                    </Stack>
                                </Stack>

                                <Grid container spacing={3} mb={4}>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Card sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #edf2f7' }}>
                                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{ p: 1.5, bgcolor: 'rgba(72, 187, 120, 0.1)', color: '#48BB78', borderRadius: 2 }}>
                                                    <GroupsIcon />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>ENROLLED STUDENTS</Typography>
                                                    <Typography variant="h5" fontWeight={800}>{tenantDetails._count?.students || 0}</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Card sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #edf2f7' }}>
                                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{ p: 1.5, bgcolor: 'rgba(49, 130, 206, 0.1)', color: '#3182CE', borderRadius: 2 }}>
                                                    <GroupsIcon />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>STAFF ACCOUNTS</Typography>
                                                    <Typography variant="h5" fontWeight={800}>{tenantDetails._count?.users || 0}</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Card sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #edf2f7' }}>
                                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{ p: 1.5, bgcolor: 'rgba(249, 180, 63, 0.1)', color: '#F9B43F', borderRadius: 2 }}>
                                                    <DescriptionIcon />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL INVOICES</Typography>
                                                    <Typography variant="h5" fontWeight={800}>{tenantDetails._count?.invoices || 0}</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <Card sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #edf2f7' }}>
                                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box sx={{ p: 1.5, bgcolor: 'rgba(128, 90, 213, 0.1)', color: '#805AD5', borderRadius: 2 }}>
                                                    <AccountBalanceWalletIcon />
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>PLAN CAPACITY</Typography>
                                                    <Typography variant="h5" fontWeight={800}>{tenantDetails.maxStudents}</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>

                                {/* Account Actions Card */}
                                <Card sx={{ bgcolor: 'white', borderRadius: 3, border: '1px solid #edf2f7', p: 3, mb: 4 }}>
                                    <Typography variant="h6" fontWeight={700} mb={3}>Subscription & Access Controls</Typography>
                                    <Stack direction="row" spacing={3}>
                                        <Box sx={{ flex: 1, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Subscription Status</Typography>
                                            <Typography variant="h6" fontWeight="bold" color={tenantDetails.status === 'ACTIVE' ? '#48BB78' : tenantDetails.status === 'SUSPENDED' ? 'error' : '#ED8936'}>
                                                {tenantDetails.status}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">Current period ends: {new Date(tenantDetails.trialEndDate).toLocaleDateString()}</Typography>
                                            <Stack direction="row" spacing={1} mt={2}>
                                                <Button 
                                                    variant="contained" 
                                                    color={tenantDetails.status === 'SUSPENDED' ? 'success' : 'error'}
                                                    size="small"
                                                    onClick={() => handleToggleStatus(tenantDetails.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED')}
                                                >
                                                    {tenantDetails.status === 'SUSPENDED' ? 'Reactivate' : 'Suspend'}
                                                </Button>
                                                <Button 
                                                    variant="outlined" 
                                                    color="warning"
                                                    size="small"
                                                    startIcon={<EmailIcon />}
                                                    onClick={() => handleResendWelcomeEmail(tenantDetails.id)}
                                                >
                                                    Resend Welcome
                                                </Button>
                                            </Stack>
                                        </Box>

                                        <Box sx={{ flex: 1, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Manual Period Extension</Typography>
                                            <Typography variant="body2" color="text.secondary" mb={2}>Push out the expiry date to grant extra trial time or handle manual renewals.</Typography>
                                            <Button variant="outlined" startIcon={<TimerIcon />} onClick={() => { setExtendDate(tenantDetails.trialEndDate.split('T')[0]); setExtendDialogOpen(true); }}>
                                                Extend Expiry Date
                                            </Button>
                                        </Box>

                                        <Box sx={{ flex: 1, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Administrative Billing</Typography>
                                            <Typography variant="body2" color="text.secondary" mb={2}>Generate special invoices for custom enterprise agreements.</Typography>
                                            <Button variant="outlined" onClick={() => { setInvoiceForm({ ...invoiceForm, tenantId: tenantDetails.id, planCode: tenantDetails.plan }); setInvoiceDialogOpen(true); }}>
                                                Create Custom Invoice
                                            </Button>
                                        </Box>
                                    </Stack>
                                </Card>

                                <Grid container spacing={4}>
                                    <Grid size={{ xs: 12, md: 5 }}>
                                        <Typography variant="h6" fontWeight={700} color="#263B7C" mb={2}>School Profile</Typography>
                                        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #edf2f7' }}>
                                            <Stack spacing={2}>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Subdomain</Typography>
                                                    <Typography fontWeight={600}>{tenantDetails.slug}.squareedge.com</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Custom Domain</Typography>
                                                    <Typography fontWeight={600}>{tenantDetails.domain || 'Not Configured'}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Subscription Tier</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Chip label={tenantDetails.plan} size="small" />
                                                    </Box>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Status</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Chip label={tenantDetails.status} color={getStatusColor(tenantDetails.status) as any} size="small" />
                                                    </Box>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Trial Period</Typography>
                                                    <Typography fontWeight={600}>{new Date(tenantDetails.trialStartDate).toLocaleDateString()} - {new Date(tenantDetails.trialEndDate).toLocaleDateString()}</Typography>
                                                </Box>
                                                <Divider />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">School Contact (from Profile)</Typography>
                                                    <Typography fontWeight={600}>{tenantDetails.schoolProfiles?.[0]?.email || 'N/A'}</Typography>
                                                    <Typography fontWeight={600}>{tenantDetails.schoolProfiles?.[0]?.phone || 'N/A'}</Typography>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 7 }}>
                                        <Typography variant="h6" fontWeight={700} color="#263B7C" mb={2}>Financial History</Typography>
                                        <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid #edf2f7' }}>
                                            <Table size="small">
                                                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                                    <TableRow>
                                                        <TableCell>Date</TableCell>
                                                        <TableCell>Amount</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell align="right">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {viewTenantInvoices.map((inv) => (
                                                        <TableRow key={inv.id}>
                                                            <TableCell>{new Date(inv.createdAt).toLocaleDateString()}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>${Number(inv.amount).toFixed(2)}</TableCell>
                                                            <TableCell><Chip label={inv.status} size="small" color={getStatusColor(inv.status) as any} /></TableCell>
                                                            <TableCell align="right">
                                                                <IconButton size="small" onClick={() => handleSendStatement(inv.id)}><SendIcon fontSize="inherit" /></IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {viewTenantInvoices.length === 0 && (
                                                        <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}>No financial records found.</TableCell></TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <TableContainer sx={{ border: '1px solid #edf2f7', borderRadius: 2 }}>
                                <Table>
                                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>School Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Slug / Subdomain</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Plan Type</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Registered</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Stats</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tenants.map((tenant) => (
                                            <TableRow key={tenant.id} hover>
                                                <TableCell sx={{ fontWeight: 600, color: '#263B7C' }}>{tenant.name}</TableCell>
                                                <TableCell>{tenant.slug}</TableCell>
                                                <TableCell><Chip label={tenant.plan} size="small" sx={{ fontWeight: 600, bgcolor: '#EBF4FF', color: '#3182CE' }} /></TableCell>
                                                <TableCell>
                                                    <Chip label={tenant.status} color={getStatusColor(tenant.status) as any} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                                </TableCell>
                                                <TableCell>{new Date(tenant.createdAt).toLocaleDateString()}</TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" display="block">Students: {tenant._count?.students || 0}</Typography>
                                                    <Typography variant="caption" display="block">Staff: {tenant._count?.users || 0}</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                        <IconButton color="primary" size="small" title="View Details" onClick={() => fetchTenantDetails(tenant.id)}>
                                                            <VisibilityIcon />
                                                        </IconButton>
                                                        <IconButton color="warning" size="small" title="Resend Welcome Email" onClick={() => handleResendWelcomeEmail(tenant.id)}>
                                                            <EmailIcon />
                                                        </IconButton>
                                                        <IconButton color="info" size="small" title="Edit Tenant" onClick={() => handleOpenEditTenant(tenant)}>
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton color="error" size="small" title="Delete Tenant" onClick={() => handleDeleteTenant(tenant.id)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CustomTabPanel>

                    {/* TAB 2: SaaS BILLING */}
                    <CustomTabPanel value={tabValue} index={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" fontWeight={700} color="#263B7C">Global Invoices</Typography>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setInvoiceDialogOpen(true)} sx={{ bgcolor: '#263B7C' }}>
                                Create Invoice
                            </Button>
                        </Box>
                        <TableContainer sx={{ border: '1px solid #edf2f7', borderRadius: 2 }}>
                            <Table>
                                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>School (Tenant)</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Billing Period</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoices.map((inv) => (
                                        <TableRow key={inv.id} hover>
                                            <TableCell>{new Date(inv.createdAt).toLocaleDateString()}</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#2D3748' }}>{inv.tenant?.name || 'Unknown'}</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>${Number(inv.amount).toFixed(2)}</TableCell>
                                            <TableCell>{new Date(inv.periodStart).toLocaleDateString()} - {new Date(inv.periodEnd).toLocaleDateString()}</TableCell>
                                            <TableCell>
                                                <Chip label={inv.status} color={getStatusColor(inv.status) as any} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                    {inv.status !== 'PAID' && (
                                                        <Button size="small" variant="contained" color="success" onClick={() => handleRecordPayment(inv.id)} sx={{ borderRadius: 2 }}>
                                                            Mark Paid
                                                        </Button>
                                                    )}
                                                    <IconButton size="small" color="primary" onClick={() => handleSendStatement(inv.id)}>
                                                        <SendIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {invoices.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>No invoices recorded.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CustomTabPanel>

                    {/* TAB 3: PLANS */}
                    <CustomTabPanel value={tabValue} index={3}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="h6" fontWeight={700} color="#263B7C">Subscription Tier Rules</Typography>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenPlanDialog()} sx={{ bgcolor: '#F9B43F', color: '#1a285a', fontWeight: 'bold' }}>
                                Add Plan
                            </Button>
                        </Box>
                        
                        <Grid container spacing={3}>
                            {plans.map((plan) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={plan.id}>
                                    <Box sx={{ p: 3, border: '1px solid #edf2f7', borderRadius: 3, position: 'relative' }}>
                                        <Typography variant="h5" fontWeight={800} color="#263B7C" mb={1}>{plan.name}</Typography>
                                        <Typography variant="h4" fontWeight={800} color="#3182CE" mb={2}>${Number(plan.price).toFixed(2)}</Typography>
                                        
                                        <Divider sx={{ my: 2 }} />
                                        
                                        <Stack spacing={1} mb={3}>
                                            <Typography variant="body2"><strong>Code:</strong> {plan.code}</Typography>
                                            <Typography variant="body2"><strong>Max Students:</strong> {plan.maxStudents}</Typography>
                                            <Typography variant="body2"><strong>Max Users:</strong> {plan.maxUsers}</Typography>
                                        </Stack>

                                        <Button fullWidth variant="outlined" onClick={() => handleOpenPlanDialog(plan)} sx={{ borderColor: '#E2E8F0', color: '#4A5568', fontWeight: 600 }}>
                                            Edit Limits
                                        </Button>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </CustomTabPanel>
                </Box>
            </Paper>

            {/* DIALOG: Manage Tenant */}
            <Dialog open={manageTenantOpen} onClose={() => setManageTenantOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: '#263B7C' }}>Manage School Tenant</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} mt={2}>
                        <FormControl fullWidth>
                            <InputLabel>Subscription Tier</InputLabel>
                            <Select
                                value={tenantForm.plan}
                                label="Subscription Tier"
                                onChange={(e) => setTenantForm({ ...tenantForm, plan: e.target.value })}
                            >
                                {plans.map(p => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Access Status</InputLabel>
                            <Select
                                value={tenantForm.status}
                                label="Access Status"
                                onChange={(e) => setTenantForm({ ...tenantForm, status: e.target.value })}
                            >
                                <MenuItem value="TRIAL">TRIAL</MenuItem>
                                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                                <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setManageTenantOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleSaveTenant} variant="contained" sx={{ bgcolor: '#263B7C' }}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG: Edit Tenant Detail */}
            <Dialog open={editTenantDialogOpen} onClose={() => setEditTenantDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: '#263B7C' }}>Edit Tenant Profile: {selectedTenant?.name}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={3} mt={1}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField label="School Name" fullWidth value={editTenantForm.name} onChange={(e) => setEditTenantForm({ ...editTenantForm, name: e.target.value })} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField label="Subdomain (slug)" fullWidth value={editTenantForm.slug} onChange={(e) => setEditTenantForm({ ...editTenantForm, slug: e.target.value })} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Subscription Tier</InputLabel>
                                <Select
                                    value={editTenantForm.plan}
                                    label="Subscription Tier"
                                    onChange={(e) => setEditTenantForm({ ...editTenantForm, plan: e.target.value })}
                                >
                                    {plans.map(p => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={editTenantForm.status}
                                    label="Status"
                                    onChange={(e) => setEditTenantForm({ ...editTenantForm, status: e.target.value })}
                                >
                                    <MenuItem value="TRIAL">TRIAL</MenuItem>
                                    <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                                    <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                                    <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField label="Max Students" type="number" fullWidth value={editTenantForm.maxStudents} onChange={(e) => setEditTenantForm({ ...editTenantForm, maxStudents: parseInt(e.target.value) })} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField label="Max Users / Staff" type="number" fullWidth value={editTenantForm.maxUsers} onChange={(e) => setEditTenantForm({ ...editTenantForm, maxUsers: parseInt(e.target.value) })} />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField label="Custom Domain" fullWidth value={editTenantForm.domain} onChange={(e) => setEditTenantForm({ ...editTenantForm, domain: e.target.value })} placeholder="e.g. school.example.com" />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setEditTenantDialogOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleUpdateTenant} variant="contained" sx={{ bgcolor: '#263B7C' }}>Update School Profile</Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG: Manage Plan */}
            <Dialog open={planDialogOpen} onClose={() => setPlanDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: '#263B7C' }}>{selectedPlan ? 'Edit Plan' : 'Create Plan'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} mt={2}>
                        <TextField label="Plan Name" fullWidth value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} />
                        <TextField label="Plan Code (e.g. BASIC)" fullWidth value={planForm.code} onChange={(e) => setPlanForm({ ...planForm, code: e.target.value })} disabled={!!selectedPlan} />
                        <TextField label="Monthly Price" type="number" fullWidth value={planForm.price} onChange={(e) => setPlanForm({ ...planForm, price: parseFloat(e.target.value) })} />
                        <TextField label="Max Students Limit" type="number" fullWidth value={planForm.maxStudents} onChange={(e) => setPlanForm({ ...planForm, maxStudents: parseInt(e.target.value) })} />
                        <TextField label="Max User / Staff Accounts" type="number" fullWidth value={planForm.maxUsers} onChange={(e) => setPlanForm({ ...planForm, maxUsers: parseInt(e.target.value) })} />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setPlanDialogOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleSavePlan} variant="contained" sx={{ bgcolor: '#263B7C' }}>Save Plan</Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG: Create Invoice */}
            <Dialog open={invoiceDialogOpen} onClose={() => setInvoiceDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: '#263B7C' }}>Bill SaaS Tenant</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} mt={2}>
                        <FormControl fullWidth>
                            <InputLabel>Select Tenant School</InputLabel>
                            <Select
                                value={invoiceForm.tenantId}
                                label="Select Tenant School"
                                onChange={(e) => {
                                    const tenant = tenants.find(t => t.id === e.target.value);
                                    const plan = plans.find(p => p.code === tenant?.plan);
                                    setInvoiceForm({ 
                                        ...invoiceForm, 
                                        tenantId: e.target.value,
                                        planCode: tenant?.plan || '',
                                        amount: plan?.price || 0
                                    });
                                }}
                            >
                                {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name} ({t.slug})</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Billing Plan / Service</InputLabel>
                            <Select
                                value={invoiceForm.planCode}
                                label="Billing Plan / Service"
                                onChange={(e) => {
                                    const plan = plans.find(p => p.code === e.target.value);
                                    setInvoiceForm({ 
                                        ...invoiceForm, 
                                        planCode: e.target.value, 
                                        amount: plan?.price || 0 
                                    });
                                }}
                            >
                                {plans.map(p => <MenuItem key={p.code} value={p.code}>{p.name} (${p.price})</MenuItem>)}
                                <MenuItem value="CUSTOM">Custom Amount</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField 
                            label="Billed Amount" 
                            type="number" 
                            fullWidth 
                            value={invoiceForm.amount} 
                            onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: parseFloat(e.target.value) })} 
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField 
                                label="Period Start" 
                                type="date" 
                                fullWidth 
                                InputLabelProps={{ shrink: true }} 
                                value={invoiceForm.periodStart} 
                                onChange={(e) => setInvoiceForm({ ...invoiceForm, periodStart: e.target.value })} 
                            />
                            <TextField 
                                label="Period End" 
                                type="date" 
                                fullWidth 
                                InputLabelProps={{ shrink: true }} 
                                value={invoiceForm.periodEnd} 
                                onChange={(e) => setInvoiceForm({ ...invoiceForm, periodEnd: e.target.value })} 
                            />
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setInvoiceDialogOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleCreateInvoice} variant="contained" sx={{ bgcolor: '#F9B43F', color: '#1a285a', fontWeight: 'bold' }}>Generate Invoice</Button>
                </DialogActions>
            </Dialog>

            {/* DIALOG: Extend Trial / Subscription */}
            <Dialog open={extendDialogOpen} onClose={() => setExtendDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: '#263B7C' }}>Extend School Access</DialogTitle>
                <DialogContent>
                    <Box mt={2}>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                            Set a new expiration date for this school. This will allow them continued access to the platform.
                        </Typography>
                        <TextField 
                            label="New Expiry Date" 
                            type="date" 
                            fullWidth 
                            InputLabelProps={{ shrink: true }} 
                            value={extendDate} 
                            onChange={(e) => setExtendDate(e.target.value)} 
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setExtendDialogOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleExtendExpiry} variant="contained" sx={{ bgcolor: '#263B7C' }}>Update Access Period</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SaaSAdminDashboard;
